import ErrorAlert from "../../common/ErrorAlert";
import { Stage } from "../../../lusca-search-api/generated-src";
import { ExecutionsTable } from "../table/executionsTable";
import { RedriveItemsTable } from "../table/redriveItemsTable";
import { RedriveExecutionDetails } from "./RedriveExecutionDetails";
import { useRedriveData } from "../hooks/useRedriveData";
import { useRedriveItemsData } from "../hooks/useRedriveItemsData";
import { RedriveKind } from "../redriveKinds";

interface Props {
  kind: RedriveKind;
  stage: Stage;
  selectedExecutionId: string | null;
  onSelectExecution: (executionId: string) => void;
}

/** Past executions of a redrive kind, or the items in the selected execution. */
export function RedriveHistory({
  kind,
  stage,
  selectedExecutionId,
  onSelectExecution,
}: Props) {
  const {
    pastRedrives,
    loading: loadingRedrives,
    error: fetchError,
  } = useRedriveData(stage, kind);
  const {
    items,
    loading: loadingItems,
    error: itemsError,
    isTruncated,
    downloadUrl,
    metadata,
  } = useRedriveItemsData(stage, selectedExecutionId, kind);

  const error = [fetchError, itemsError].filter(Boolean).join("; ");

  // Prefer the already-loaded table row so table clicks render instantly; fall
  // back to the API metadata for deep-links not present in the history list.
  const listItem = pastRedrives.find(
    (item) => item.executionId === selectedExecutionId
  );
  const resolvedMetadata = selectedExecutionId
    ? {
        executor: listItem?.executor ?? metadata?.executor,
        status: listItem?.status ?? metadata?.status,
        requestDate: listItem?.executionDate
          ? new Date(listItem.executionDate)
          : metadata?.timestamp
          ? new Date(metadata.timestamp * 1000)
          : undefined,
        count: listItem?.itemCount
          ? Number(listItem.itemCount)
          : metadata?.count,
      }
    : null;

  return (
    <>
      {error && <ErrorAlert content={error} header="Redrive History Error" />}
      {selectedExecutionId ? (
        <>
          <RedriveExecutionDetails
            kind={kind}
            metadata={resolvedMetadata}
            loading={!listItem && loadingItems}
          />
          <RedriveItemsTable
            kind={kind}
            items={items}
            loading={loadingItems}
            executionId={selectedExecutionId}
            stage={stage}
            user={listItem?.user}
            isTruncated={isTruncated}
            downloadUrl={downloadUrl}
          />
        </>
      ) : (
        <ExecutionsTable
          kind={kind}
          items={pastRedrives}
          loading={loadingRedrives}
          onSelectExecution={onSelectExecution}
        />
      )}
    </>
  );
}
